import { formatCurrency } from '@if/core/utils'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

interface LoanCalendarActionPreviewSummaryCompactProps {
  simulationBefore: CalendarSimulation
  simulationAfter: CalendarSimulation
  actionDate?: Date
}

const LoanCalendarActionPreviewSummaryCompact = memo(
  ({ simulationBefore, simulationAfter }: LoanCalendarActionPreviewSummaryCompactProps) => {
    const { t } = useTranslation()

    const stats = useMemo(() => {
      const beforeOp = simulationBefore.summaryInformation
      const afterOp = simulationAfter.summaryInformation

      return {
        lateDaysBefore: beforeOp?.daysLate || 0,
        lateDaysAfter: afterOp?.daysLate || 0,
        lateCapitalBefore: beforeOp?.outstandingBalance || 0,
        lateCapitalAfter: afterOp?.outstandingBalance || 0,
      }
    }, [simulationBefore, simulationAfter])

    const getChipColor = (before: number, after: number) => {
      if (after > before) return 'error'
      if (after < before) return 'success'
      return 'default'
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto 1fr',
          gap: 2,
          alignItems: 'center',
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        {/* Late Days */}
        <Typography variant="caption" fontWeight="medium" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {t('loan.payment.preview.nbLateDays')}:
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={t('common.days', { count: stats.lateDaysBefore })} size="small" variant="outlined" />
          <ArrowForwardIcon fontSize="small" color="disabled" />
          <Chip
            label={t('common.days', { count: stats.lateDaysAfter })}
            size="small"
            color={getChipColor(stats.lateDaysBefore, stats.lateDaysAfter)}
          />
        </Stack>

        {/* Late Capital */}
        <Typography variant="caption" fontWeight="medium" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {t('loan.payment.preview.latePrincipal')}:
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={formatCurrency(stats.lateCapitalBefore)} size="small" variant="outlined" />
          <ArrowForwardIcon fontSize="small" color="disabled" />
          <Chip
            label={formatCurrency(stats.lateCapitalAfter)}
            size="small"
            color={getChipColor(stats.lateCapitalBefore, stats.lateCapitalAfter)}
          />
        </Stack>
      </Box>
    )
  },
)

LoanCalendarActionPreviewSummaryCompact.displayName = 'LoanCalendarActionPreviewSummaryCompact'

export { LoanCalendarActionPreviewSummaryCompact }
